import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar as MuiToolbar,
  Typography,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
  Menu,
  Rating,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  GetApp as DownloadIcon,
  InfoOutlined as InfoIcon,
  Phone,
  Email,
  Star,
  Female,
  Male,
  HelpOutline,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";

const getGenderIcon = (gender) => {
  if (gender?.toLowerCase() === "hombre") return <Male fontSize="small" />;
  if (gender?.toLowerCase() === "mujer") return <Female fontSize="small" />;
  return <HelpOutline fontSize="small" />;
};
const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};
const CustomerInfoTooltip = ({ customer }) => (
  <Box sx={{ p: 1, minWidth: 260 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
      {customer?.fullName || "Cliente Desconocido"}
    </Typography>
    <Divider sx={{ my: 1 }} />
    <Box mt={1}>
      <Box display="flex" alignItems="center" mb={0.5}>
        <Phone fontSize="small" />
        <Typography variant="body2" fontWeight="bold" sx={{ ml: 1 }}>
          Teléfono:
        </Typography>
        <Typography variant="body2" sx={{ ml: 1 }}>
          {customer?.phone || "No disponible"}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" mb={0.5}>
        <Email fontSize="small" />
        <Typography variant="body2" fontWeight="bold" sx={{ ml: 1 }}>
          Correo:
        </Typography>
        <Typography variant="body2" sx={{ ml: 1 }}>
          {customer?.email || "No disponible"}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" mb={0.5}>
        {getGenderIcon(customer?.gender)}
        <Typography variant="body2" fontWeight="bold" sx={{ ml: 1 }}>
          Género:
        </Typography>
        <Typography variant="body2" sx={{ ml: 1 }}>
          {capitalizeFirstLetter(customer?.gender) || "No especificado"}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" mb={0.5}>
        <Star fontSize="small" />
        <Typography variant="body2" fontWeight="bold" sx={{ ml: 1 }}>
          Calificación:
        </Typography>
        <Typography variant="body2" sx={{ ml: 1 }}>
          {customer?.rating === undefined ? (
            "No tiene"
          ) : (
            <Rating
              name="customer-rating"
              value={customer?.rating || 0}
              readOnly
              precision={0.5}
              sx={{ fontSize: "1.2rem" }}
            />
          )}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ToolBarResponse = ({
  forms = [],
  selectedFormId,
  onSelectForm,
  formName = "",
  onSaveResponse,
  onDownload,
  onBack,
  saving = false,
  loadingForms = false,
  customer,
  mode = "create",
  responseName,
  onResponseNameChange,
}) => {
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(responseName || "Nuevo Formulario");
  const navigate = useNavigate();

  // Sincronizar tempName si cambia el prop
  React.useEffect(() => {
    setTempName(responseName || "Nuevo Formulario");
  }, [responseName]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleDownloadSelect = (format) => {
    if (onDownload) onDownload(format);
    handleDownloadClose();
  };

  const handleNameEdit = () => {
    setIsEditingName(true);
    setTempName(responseName || "Nuevo Formulario");
  };
  const handleNameSave = () => {
    onResponseNameChange(tempName);
    setIsEditingName(false);
  };
  const handleNameCancel = () => {
    setTempName(responseName || "Nuevo Formulario");
    setIsEditingName(false);
  };
  const handleNameKeyPress = (e) => {
    if (e.key === "Enter") handleNameSave();
    else if (e.key === "Escape") handleNameCancel();
  };

  return (
    <AppBar
      position="static"
      elevation={1}
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <MuiToolbar sx={{ minHeight: "64px !important", justifyContent: "space-between" }}>
        {/* Lado Izquierdo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: "250px" }}>
          <Tooltip title="Volver">
            <IconButton onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          {mode === "create" && (
            <Select
              value={selectedFormId}
              onChange={(e) => onSelectForm && onSelectForm(e.target.value)}
              displayEmpty
              fullWidth
              size="small"
              disabled={mode !== "create"}
              renderValue={(selected) => {
                if (selected === "") {
                  return <em>Selecciona un formulario...</em>;
                }
                const selectedForm = forms.find((form) => form._id === selected);
                return selectedForm ? selectedForm.name : "";
              }}
            >
              {loadingForms ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Cargando...
                </MenuItem>
              ) : (
                [
                  <MenuItem key="placeholder" value="" disabled>
                    <em>Selecciona un formulario...</em>
                  </MenuItem>,
                  ...forms.map((form) => (
                    <MenuItem key={form._id || form.id} value={form._id || form.id}>
                      {form.name}
                    </MenuItem>
                  )),
                ]
              )}
            </Select>
          )}
        </Box>

        {/* Centro (Título) */}
        <Box sx={{ flex: 1, textAlign: "center" }}>
          {isEditingName && mode !== "view" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
              <TextField
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onKeyDown={handleNameKeyPress}
                size="small"
                autoFocus
                sx={{ minWidth: 200 }}
                disabled={mode === "view"}
              />
              <IconButton size="small" onClick={handleNameSave} color="primary" disabled={mode === "view"}>
                <CheckIcon />
              </IconButton>
              <IconButton size="small" onClick={handleNameCancel} disabled={mode === "view"}>
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }} noWrap>
                {responseName || "Nuevo Formulario"}
              </Typography>
              {mode !== "view" && (
                <IconButton size="small" onClick={handleNameEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        {/* Lado Derecho */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: "250px", justifyContent: "flex-end" }}>
          {customer && (
            <Tooltip title={<CustomerInfoTooltip customer={customer} />} placement="bottom" arrow>
              <IconButton>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          )}
          <Divider orientation="vertical" flexItem />

          <Tooltip title="Descargar respuesta">
            <span>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                size="small"
                disabled={!selectedFormId}
              >
                Descargar
              </Button>
            </span>
          </Tooltip>
          <Menu anchorEl={downloadAnchorEl} open={Boolean(downloadAnchorEl)} onClose={handleDownloadClose}>
            <MenuItem onClick={() => handleDownloadSelect("json")}>Descargar como JSON</MenuItem>
            <MenuItem onClick={() => handleDownloadSelect("pdf")}>Descargar como PDF</MenuItem>
          </Menu>

          {mode !== "view" && (
            <Tooltip title="Guardar respuesta">
              <span>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={onSaveResponse}
                  disabled={saving || !selectedFormId}
                  sx={{ minWidth: 120 }}
                  size="small"
                >
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </MuiToolbar>
    </AppBar>
  );
};

export default ToolBarResponse;
